import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAnalytics, getAnalytics } from '@angular/fire/analytics';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideClientHydration } from '@angular/platform-browser';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localeIn from '@angular/common/locales/en-IN';
import {
  provideRouter,
  RouteReuseStrategy,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideToastr } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, take, tap, timeout } from 'rxjs/operators';
import { ApiService } from './services/api.service';
import { CachingService } from './services/caching.service';
import { environment } from '../environments/environment';
import { firebaseConfig } from './constants/firebase-config';
import { RouteReuseConfig } from './route-config/route-reuse.config';
import { attachXAPIKeyInterceptor } from './interceptors/attach-x-api-key.interceptor';
import { EncryptDecryptInterceptor } from './interceptors/encrypt-decrypt.interceptor';
import { sanitizeRequestInterceptor } from './interceptors/sanitize-request.interceptor';
import { appRoutes } from './app.routes';
import { attachEncodedDataKeyInterceptor } from './interceptors/attach-encoded-data-key.interceptor';
import { cachingKeys } from './constants/caching-keys';
import { provideHighlightOptions } from 'ngx-highlightjs';
import { routes } from './constants/routes';
import { AttachTokenInterceptor } from './interceptors/attach-token.interceptor';
import { PosthogService } from './services/posthog.service';

registerLocaleData(localeIn, 'en-IN');

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, '../i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withInMemoryScrolling({
        get scrollPositionRestoration() {
          let result: 'disabled' | 'enabled' = 'enabled';

          try {
            // Ensure this runs only in the browser.
            if (typeof window === 'undefined') {
              throw new Error('This is server env.');
            }

            const isProfileRoute = window.location.pathname.includes(
              routes.publicProfile,
            );

            // If app is not hydrated yet.
            if (!(<any>window).__hasHydrated || isProfileRoute) {
              const { pathname } = window.location;
              const isRouteHome = ['/home', '/home/', '/'].includes(pathname);

              // If it's the home route, disable scroll restoration.
              if (isRouteHome || isProfileRoute) {
                result = 'disabled';
              } else {
                // Mark as hydrated for subsequent loads.
                (<any>window).__hasHydrated = true;
              }
            }
          } catch (_) {
            // Scroll restoration ran on server.
          } finally {
            return result;
          }
        },
      }),
    ),
    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi(),
      withInterceptors([
        attachXAPIKeyInterceptor,
        sanitizeRequestInterceptor,
        attachEncodedDataKeyInterceptor,
      ]),
    ),
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    provideClientHydration(),
    provideToastr({
      easeTime: 500,
      timeOut: 3000,
      positionClass: 'toast-bottom-right',
      progressBar: true,
      closeButton: true,
      tapToDismiss: true,
    }),
    provideFirebaseApp(() => initializeApp(firebaseConfig)),
    provideAnalytics(() => getAnalytics()),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    {
      provide: APP_BASE_HREF,
      useValue: environment.baseHref,
    },
    {
      // Dynamically fetching master APIs slugs.
      provide: APP_INITIALIZER,
      useFactory: (apiService: ApiService, cachingService: CachingService) => {
        return () =>
          apiService.fetchMasterTypes().pipe(
            take(1),
            timeout(5000),
            tap((types) => {
              const { slug: sectorsSlug } = types.find(({ id }) => id === 2);
              const { slug: licensesSlug } = types.find(({ id }) => id === 3);
              cachingService.masterTypes.sectors = sectorsSlug;
              cachingService.masterTypes.licenses = licensesSlug;
            }),
            catchError((_) => of(null)),
          );
      },
      deps: [ApiService, CachingService],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: 'en-IN',
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EncryptDecryptInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AttachTokenInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (apiService: ApiService, cachingService: CachingService) => {
        return () =>
          apiService.fetchMaintenance().pipe(
            take(1),
            timeout(5000),
            tap((maintenance) => {
              cachingService.setCache(cachingKeys.maintenance, maintenance);
            }),
            catchError((_) => of(null)),
          );
      },
      deps: [ApiService, CachingService],
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: RouteReuseConfig,
    },
    provideHighlightOptions({
      fullLibraryLoader: () => import('highlight.js'),
      lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'),
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: (posthogService: PosthogService) => {
        return () => {
          posthogService.init();
        };
      },
      deps: [PosthogService],
      multi: true,
    },
  ],
};
